import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useTranslation from '../../customHooks/translations';
import ListErrors from '../ListErrors';
import { DEFAULT_THEME_COLOR, FREAK_HUB_COLOR } from '../../constants/theme';
import Typography from '@mui/material/Typography';
import FreakParticipantsList from '../Freak/FreakParticipantsList';
import FreakSelect from '../Freak/FreakSelect';
import agent from '../../agent';
import { FREAK_HUB_SUBMITTED } from '../../constants/actionTypes';
import { convertToBase64 } from '../../utils/imageUtils';
import InputBase from '@mui/material/InputBase';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HubIcon from '@mui/icons-material/Hub';
import PublicIcon from '@mui/icons-material/Public';
import {
  COMPOSER_COLORS,
  ComposerAddBar,
  ComposerInfoLine,
  ComposerPanel,
  ComposerShell,
  ComposerTitleInput,
  composerText,
} from '../Freak/FreakComposerParts';

const AVATAR_SIZE = 96;

// ─── Component ────────────────────────────────────────────────────────────────

const FreakHubEditor = (props) => {
  const translation = useTranslation();
  const t = translation.hubEditor;
  const tf = translation.freakEditor;
  const navigate = useNavigate();
  const { slug } = useParams();

  const [freakHubId, setFreakHubId] = useState(null);
  const [title, setTitle] = useState('');
  const [oldTitle, setOldTitle] = useState('');
  const [body, setBody] = useState('');
  const [participants, setParticipants] = useState([]);
  const [author, setAuthor] = useState(null);
  const [isAuthor, setIsAuthor] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  // a new image is a base64 data url (the API uploads it); null keeps the saved one
  const [newImage, setNewImage] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [newBackgroundImage, setNewBackgroundImage] = useState(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');

  const imageInput = useRef(null);
  const coverInput = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      if (slug) {
        const formData = await agent.FreakHubs.get(slug, props.device);
        const hub = formData?.freakHub;
        if (hub) {
          setFreakHubId(hub.id);
          setTitle(hub.name || '');
          setOldTitle(hub.name || '');
          setBody(hub.description || '');
          setParticipants(hub.participants || []);
          setAuthor(hub.author);
          setIsAuthor(hub.author?.username === props?.currentUser?.username);
          setImageUrl(hub.imageUrl ?? '');
          setBackgroundImageUrl(hub.backgroundImageUrl ?? '');
        }
      }
    };
    fetchData();
  }, []);

  const canSave = !!title.trim() && !!body.trim();

  const onPicked = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const dataUrl = await convertToBase64(file);
    setIsDirty(true);
    if (kind === 'cover') {
      setNewBackgroundImage(dataUrl);
      setBackgroundImageUrl(dataUrl);
    } else {
      setNewImage(dataUrl);
      setImageUrl(dataUrl);
    }
  };

  const handleParticipantSelect = (incoming) => {
    const updated = [...participants];
    for (const p of incoming) {
      if (!updated.find(u => u.username === p.username)) updated.push(p);
    }
    setIsDirty(true);
    setParticipants(updated);
  };

  const submitForm = (event) => {
    event.preventDefault();
    if (!canSave) return;
    const freakHub = {
      title,
      description: body,
      context: props.context,
      body,
      tagList: [],
      participants: participants.map(p => p.username),
      public: true,
      oldTitle,
    };

    if (newImage) freakHub.image = newImage;
    if (newBackgroundImage) freakHub.backgroundImage = newBackgroundImage;

    const slugObj = { slug };
    const promise = slug
      ? agent.FreakHubs.update(Object.assign(freakHub, slugObj))
      : agent.FreakHubs.create(freakHub);

    setIsDirty(false);
    props.onSubmit(promise);
  };

  const openMembers = () => {
    setIsMembersOpen(true);
    setTimeout(() => document.getElementById('composer-panel-members')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 50);
  };

  const options = [
    { key: 'cover', icon: <WallpaperIcon />, color: COMPOSER_COLORS.photo, label: t.addCover, onClick: () => coverInput.current?.click() },
    { key: 'photo', icon: <AccountBoxIcon />, color: COMPOSER_COLORS.hub, label: t.addPhoto, onClick: () => imageInput.current?.click() },
    { key: 'members', icon: <GroupAddIcon />, color: COMPOSER_COLORS.people, label: t.inviteMembers, onClick: openMembers, active: isMembersOpen || participants.length > 0 },
  ];

  return (
    <ComposerShell
      title={slug ? t.editTitle : t.createTitle}
      actionLabel={slug ? t.update : t.createAction}
      canSubmit={canSave}
      onSubmit={submitForm}
      isDirty={isDirty}
      onLeave={() => navigate(-1)}
      leftNavigationOpen={props.leftNavigationOpen}
      errors={<ListErrors errors={props.errors} />}
      hint={!canSave ? t.required : null}
      texts={{ discardTitle: t.discardTitle, discardMessage: tf.discardMessage, keepEditing: tf.keepEditing, discard: tf.discard }}
      cardSx={{ p: 0, overflow: 'hidden', '& > :not(:first-of-type)': { mx: { xs: 2, sm: 3 } }, pb: { xs: 2, sm: 3 } }}
    >
      {/* ── Cover with the round hub photo on top, like a group page ── */}
      <Box sx={{ position: 'relative', mb: `${AVATAR_SIZE / 2 + 8}px` }}>
        <ButtonBase
          data-testid="hub-cover"
          onClick={() => coverInput.current?.click()}
          aria-label={backgroundImageUrl ? t.changeCover : t.addCover}
          sx={{
            width: '100%', height: { xs: 160, sm: 220 }, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: backgroundImageUrl
              ? `center / cover no-repeat url("${backgroundImageUrl}")`
              : 'linear-gradient(135deg, rgba(249,115,0,0.25) 0%, rgba(54,201,162,0.2) 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 2, color: 'white',
              fontWeight: 600, background: 'rgba(0,0,0,0.45)',
              ...(backgroundImageUrl ? { position: 'absolute', right: 12, bottom: 12 } : {}),
            }}
          >
            <PhotoCameraIcon fontSize="small" />
            {backgroundImageUrl ? t.changeCover : t.addCover}
          </Box>
        </ButtonBase>
        <ButtonBase
          data-testid="hub-photo"
          onClick={() => imageInput.current?.click()}
          aria-label={t.addPhoto}
          sx={{ position: 'absolute', left: { xs: 16, sm: 24 }, bottom: -AVATAR_SIZE / 2, borderRadius: '50%' }}
        >
          <Avatar
            src={imageUrl || undefined}
            sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, bgcolor: FREAK_HUB_COLOR, border: '4px solid #0f1f2e' }}
          >
            <HubIcon sx={{ fontSize: 44 }} />
          </Avatar>
          <Box
            sx={{
              position: 'absolute', right: 0, bottom: 0, width: 30, height: 30, borderRadius: '50%',
              background: DEFAULT_THEME_COLOR, border: '2px solid #0f1f2e', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 16 }} />
          </Box>
        </ButtonBase>
        <input ref={coverInput} data-testid="hub-cover-input" type="file" accept="image/*" hidden onChange={onPicked('cover')} />
        <input ref={imageInput} data-testid="hub-photo-input" type="file" accept="image/*" hidden onChange={onPicked('image')} />
      </Box>

      {/* ── Name · visibility · description ── */}
      <ComposerTitleInput
        testId="hub-name"
        placeholder={t.namePlaceholder}
        value={title}
        onChange={(value) => {
          setIsDirty(true);
          setTitle(value);
        }}
      />
      <ComposerInfoLine icon={<PublicIcon />}>{t.publicInfo}</ComposerInfoLine>
      <InputBase
        fullWidth
        multiline
        minRows={3}
        inputProps={{ 'data-testid': 'hub-description' }}
        placeholder={t.descriptionPlaceholder}
        value={body}
        onChange={(e) => {
          setIsDirty(true);
          setBody(e.target.value);
        }}
        sx={{ mt: 2, color: composerText.primary, fontSize: '1.05rem' }}
      />

      {/* ── Members ── */}
      {(isMembersOpen || participants.length > 0) ? (
        <ComposerPanel id="composer-panel-members" icon={<GroupAddIcon />} color={COMPOSER_COLORS.people}
                       title={`${t.members}${participants.length ? ` · ${participants.length}` : ''}`}
                       onClose={participants.length ? undefined : () => setIsMembersOpen(false)}>
          {participants.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <FreakParticipantsList freakHubId={freakHubId} author={author} participants={participants} isAuthor={isAuthor} />
            </Box>
          )}
          <FreakSelect onSelect={handleParticipantSelect} />
        </ComposerPanel>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Button data-testid="hub-invite" startIcon={<GroupAddIcon />} onClick={openMembers} sx={{ color: DEFAULT_THEME_COLOR }}>
            <Typography component="span" sx={{ fontWeight: 600 }}>{t.inviteMembers}</Typography>
          </Button>
        </Box>
      )}

      {/* ── Add to your hub ── */}
      <ComposerAddBar label={t.addToHub} options={options} />
    </ComposerShell>
  );
};

const mapStateToProps = state => ({
  errors: state.freakHubEditor.errors,
  context: state.common.context,
  device: state.common.device,
  currentUser: state.common.currentUser,
  leftNavigationOpen: state.common.leftNavigationOpen,
});

const mapDispatchToProps = dispatch => ({
  onSubmit: payload => dispatch({ type: FREAK_HUB_SUBMITTED, payload }),
});

export default connect(mapStateToProps, mapDispatchToProps)(FreakHubEditor);
